import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

const NonSalesDetailsDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  glAccount,
  memoDetail,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showOrderGuid, setShowOrderGuid] = useState(false);
  const [orderBy, setOrderBy] = useState('amount');
  const [order, setOrder] = useState('desc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredDetails = useMemo(() => {
    if (!details.length) return details;
    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;
    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        const cellValue = String(row[columnId] ?? '').toLowerCase();
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric sorting
      if (['amount', 'orderNumber', 'salesTax', 'serviceCharges', 'donations', 'giftCardSales'].includes(orderBy)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  useEffect(() => {
    if (open && businessDate && storeId && glAccount) {
      fetchDetails();
    }
  }, [open, businessDate, storeId, glAccount]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);

      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const response = await axios.get('/hd-api/gl-export/non-sales-details', {
        params: {
          businessDate,
          storeId,
          glAccount,
        },
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
      });

      setDetails(response.data.data || []);
    } catch (err) {
      console.error('Error fetching non-sales details:', err);
      setError('Failed to load non-sales details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get category label based on GL account
  const getCategoryLabel = () => {
    switch (glAccount) {
      case '402000':
        return 'Service Charges';
      case '210900':
        return 'Sales Tax';
      case '212900':
        return 'Donations';
      case '210200':
        return 'Gift Cards Sold';
      default:
        return memoDetail || 'Non-Sales Details';
    }
  };

  // Get icon for the category
  const getCategoryIcon = () => {
    switch (glAccount) {
      case '402000':
        return <ReceiptLongIcon color="primary" />;
      case '210900':
        return <AccountBalanceIcon color="warning" />;
      case '212900':
        return <VolunteerActivismIcon sx={{ color: '#e91e63' }} />;
      case '210200':
        return <CardGiftcardIcon color="action" />;
      default:
        return <ReceiptLongIcon color="primary" />;
    }
  };

  // Get amount column label based on GL account
  const getAmountLabel = () => {
    switch (glAccount) {
      case '402000':
        return 'Service Charge';
      case '210900':
        return 'Tax Amount';
      case '212900':
        return 'Donation';
      case '210200':
        return 'Gift Card';
      default:
        return 'Amount';
    }
  };

  // Export to Excel
  const handleExport = () => {
    const exportData = sortedDetails.map((item) => ({
      'Order #': item.orderNumber,
      [getAmountLabel()]: parseFloat(item.amount) || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Non-Sales Details');
    XLSX.writeFile(wb, `non-sales-details-${storeId}-${businessDate}.xlsx`);
  };

  // Calculate totals
  const totals = useMemo(() => {
    return filteredDetails.reduce(
      (acc, d) => ({
        amount: acc.amount + (parseFloat(d.amount) || 0),
        salesTax: acc.salesTax + (parseFloat(d.salesTax) || 0),
        serviceCharges: acc.serviceCharges + (parseFloat(d.serviceCharges) || 0),
        donations: acc.donations + (parseFloat(d.donations) || 0),
        giftCardSales: acc.giftCardSales + (parseFloat(d.giftCardSales) || 0),
      }),
      { amount: 0, salesTax: 0, serviceCharges: 0, donations: 0, giftCardSales: 0 }
    );
  }, [filteredDetails]);

  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Count ezCater orders
  const ezCaterCount = useMemo(() => {
    return details.filter((d) => d.isEzCater).length;
  }, [details]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getCategoryIcon()}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            {getCategoryLabel()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            Store {storeId} - {businessDate} - GL {glAccount}
          </Typography>
        </Box>
        {ezCaterCount > 0 && (
          <Chip
            label={`${ezCaterCount} ezCater`}
            size="small"
            color="info"
            variant="outlined"
          />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && (
          <Alert severity="info">
            No {getCategoryLabel().toLowerCase()} found for this date.
          </Alert>
        )}

        {!loading && !error && details.length > 0 && (
          <TableContainer sx={{ maxHeight: 400, userSelect: 'text' }} onCopy={handleCopy}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {showOrderGuid && (
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'orderGuid'}
                        direction={orderBy === 'orderGuid' ? order : 'asc'}
                        onClick={() => handleRequestSort('orderGuid')}
                      >
                        Order GUID
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                    <TableSortLabel
                      active={orderBy === 'orderNumber'}
                      direction={orderBy === 'orderNumber' ? order : 'asc'}
                      onClick={() => handleRequestSort('orderNumber')}
                    >
                      Order #
                    </TableSortLabel>
                  </TableCell>
                  {/* State column - hide for Gift Cards */}
                  {glAccount !== '210200' && (
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'state'}
                        direction={orderBy === 'state' ? order : 'asc'}
                        onClick={() => handleRequestSort('state')}
                      >
                        State
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                    <TableSortLabel
                      active={orderBy === 'amount'}
                      direction={orderBy === 'amount' ? order : 'asc'}
                      onClick={() => handleRequestSort('amount')}
                    >
                      {getAmountLabel()}
                    </TableSortLabel>
                  </TableCell>
                  {/* Tax column - hide for Sales Tax and Gift Cards */}
                  {glAccount !== '210900' && glAccount !== '210200' && (
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'salesTax'}
                        direction={orderBy === 'salesTax' ? order : 'asc'}
                        onClick={() => handleRequestSort('salesTax')}
                      >
                        Tax
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {/* Service Charge column - hide for Service Charges and Gift Cards */}
                  {glAccount !== '402000' && glAccount !== '210200' && (
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'serviceCharges'}
                        direction={orderBy === 'serviceCharges' ? order : 'asc'}
                        onClick={() => handleRequestSort('serviceCharges')}
                      >
                        Svc Chg
                      </TableSortLabel>
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  {showOrderGuid && (
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.orderGuid || ''}
                        onChange={(e) => handleColumnFilterChange('orderGuid', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.orderGuid && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('orderGuid')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                    <TextField
                      size="small"
                      placeholder="Filter..."
                      value={columnFilters.orderNumber || ''}
                      onChange={(e) => handleColumnFilterChange('orderNumber', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FilterListIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: columnFilters.orderNumber && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => clearColumnFilter('orderNumber')}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  {/* State filter - hide for Gift Cards */}
                  {glAccount !== '210200' && (
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.state || ''}
                        onChange={(e) => handleColumnFilterChange('state', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.state && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('state')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                    <TextField
                      size="small"
                      placeholder="Filter..."
                      value={columnFilters.amount || ''}
                      onChange={(e) => handleColumnFilterChange('amount', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FilterListIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: columnFilters.amount && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => clearColumnFilter('amount')}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  {/* Tax filter - hide for Sales Tax and Gift Cards */}
                  {glAccount !== '210900' && glAccount !== '210200' && (
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.salesTax || ''}
                        onChange={(e) => handleColumnFilterChange('salesTax', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.salesTax && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('salesTax')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                  )}
                  {/* Service Charge filter - hide for Service Charges and Gift Cards */}
                  {glAccount !== '402000' && glAccount !== '210200' && (
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.serviceCharges || ''}
                        onChange={(e) => handleColumnFilterChange('serviceCharges', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.serviceCharges && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('serviceCharges')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedDetails.map((detail, index) => (
                  <TableRow
                    key={`${detail.orderGuid}-${index}`}
                    sx={{
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                    }}
                  >
                    {showOrderGuid && (
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {detail.orderGuid || '-'}
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {detail.orderNumber || '-'}
                        {detail.isEzCater && (
                          <Chip label="EC" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    {/* State - hide for Gift Cards */}
                    {glAccount !== '210200' && (
                      <TableCell>{detail.state || '-'}</TableCell>
                    )}
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 500,
                      }}
                    >
                      {formatCurrency(detail.amount)}
                    </TableCell>
                    {/* Tax - hide for Sales Tax and Gift Cards */}
                    {glAccount !== '210900' && glAccount !== '210200' && (
                      <TableCell align="right">{formatCurrency(detail.salesTax)}</TableCell>
                    )}
                    {/* Service Charge - hide for Service Charges and Gift Cards */}
                    {glAccount !== '402000' && glAccount !== '210200' && (
                      <TableCell align="right">{formatCurrency(detail.serviceCharges)}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {hasActiveFilters
              ? `${filteredDetails.length} of ${details.length} order${details.length !== 1 ? 's' : ''}`
              : `${details.length} order${details.length !== 1 ? 's' : ''}`}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showOrderGuid}
                onChange={(e) => setShowOrderGuid(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Show Order GUID</Typography>}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Total {getAmountLabel()}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600 }}
            >
              {formatCurrency(totals.amount)}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={details.length === 0}
          >
            Export
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Box>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default NonSalesDetailsDialog;
